import { Injectable, signal } from '@angular/core';

export type ToastLevel = 'success' | 'info' | 'warning' | 'error';

export interface ToastMessage {
  id: string;
  title: string;
  message: string;
  level: ToastLevel;
  /** Total lifetime in ms; also drives the progress bar animation. */
  durationMs: number;
  /** Errors are announced assertively; everything else is a passive status. */
  role: 'status' | 'alert';
  ariaLive: 'polite' | 'assertive';
  /** True while the exit animation plays, just before removal from the stack. */
  leaving: boolean;
}

interface ToastTimer {
  handle: ReturnType<typeof setTimeout>;
  remainingMs: number;
  startedAt: number;
}

@Injectable({
  providedIn: 'root'
})
export class ToastService {
  /** Never show more than this many toasts at once; the oldest is dropped. */
  private static readonly MAX_VISIBLE = 4;
  private static readonly DEFAULT_DURATION_MS = 4500;
  private static readonly ERROR_DURATION_MS = 6500;
  /** Keep in sync with the toast-out animation in ToastComponent. */
  private static readonly EXIT_ANIMATION_MS = 200;

  readonly messages = signal<ReadonlyArray<ToastMessage>>([]);

  private readonly timers = new Map<string, ToastTimer>();

  show(options: { title?: string; message: string; level?: ToastLevel; durationMs?: number }): void {
    const level = options.level ?? 'info';
    const durationMs = options.durationMs ?? this.defaultDuration(level);

    const toast: ToastMessage = {
      id: this.generateToastId(),
      title: options.title ?? this.defaultTitle(level),
      message: options.message,
      level,
      durationMs,
      role: level === 'error' ? 'alert' : 'status',
      ariaLive: level === 'error' ? 'assertive' : 'polite',
      leaving: false
    };

    this.messages.update((messages) => {
      // Newest first so it renders at the top of the stack.
      const next = [toast, ...messages];

      // Keep only the most recent toasts; drop (and clean up) any overflow.
      next.slice(ToastService.MAX_VISIBLE).forEach((message) => this.clearTimer(message.id));

      return next.slice(0, ToastService.MAX_VISIBLE);
    });

    this.startTimer(toast.id, durationMs);
  }

  dismiss(toastId: string): void {
    this.clearTimer(toastId);

    // Flag the toast as leaving so its exit animation can play before removal.
    let wasPresent = false;
    this.messages.update((messages) =>
      messages.map((message) => {
        if (message.id !== toastId) {
          return message;
        }

        wasPresent = true;
        return { ...message, leaving: true };
      })
    );

    if (!wasPresent) {
      return;
    }

    setTimeout(() => this.remove(toastId), ToastService.EXIT_ANIMATION_MS);
  }

  /** Pauses the auto-dismiss timer (e.g. while the pointer hovers the toast). */
  pause(toastId: string): void {
    const timer = this.timers.get(toastId);

    if (!timer) {
      return;
    }

    clearTimeout(timer.handle);
    const elapsed = Date.now() - timer.startedAt;
    timer.remainingMs = Math.max(0, timer.remainingMs - elapsed);
  }

  /** Resumes the auto-dismiss timer with the remaining time. */
  resume(toastId: string): void {
    const timer = this.timers.get(toastId);

    if (!timer) {
      return;
    }

    timer.startedAt = Date.now();
    timer.handle = setTimeout(() => this.dismiss(toastId), timer.remainingMs);
  }

  private remove(toastId: string): void {
    this.messages.update((messages) => messages.filter((message) => message.id !== toastId));
  }

  private startTimer(toastId: string, durationMs: number): void {
    const handle = setTimeout(() => this.dismiss(toastId), durationMs);
    this.timers.set(toastId, { handle, remainingMs: durationMs, startedAt: Date.now() });
  }

  private clearTimer(toastId: string): void {
    const timer = this.timers.get(toastId);

    if (timer) {
      clearTimeout(timer.handle);
      this.timers.delete(toastId);
    }
  }

  private defaultDuration(level: ToastLevel): number {
    return level === 'error' ? ToastService.ERROR_DURATION_MS : ToastService.DEFAULT_DURATION_MS;
  }

  private generateToastId(): string {
    return `toast-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  }

  private defaultTitle(level: ToastLevel): string {
    switch (level) {
      case 'success':
        return 'Success';
      case 'warning':
        return 'Warning';
      case 'error':
        return 'Error';
      case 'info':
      default:
        return 'Info';
    }
  }
}
